/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { colors } from "@packages/ui";
import type { RefObject } from "react";
import { useRef, useState } from "react";
import type {
  DragEndEvent,
  DragOptions,
  DragPreviewRenderer,
} from "react-aria";
import { useDrag } from "react-aria";

export type UseQueueItemOptions = Pick<
  DragOptions,
  "onDragMove" | "onDragEnd" | "onDragStart" | "getItems"
>;

const defaultStyle: Interpolation = {
  background: colors.transparent,
  border: 0,
  cursor: "grab",
  padding: 0,
};
const draggingStyle: Interpolation = {
  cursor: "grabbing",
};

export type UseQueueItemResult = {
  containerProps: any;
  childProps: any;
  handleProps: any;
  handleCSS: Interpolation;
  previewRef: RefObject<DragPreviewRenderer | null>;
};

export const useQueueItem = ({
  onDragMove,
  onDragEnd,
  onDragStart,
  getItems,
}: UseQueueItemOptions): UseQueueItemResult => {
  const previewRef = useRef<DragPreviewRenderer>(null);
  const [enableDrag, setEnableDrag] = useState<boolean>(false);

  const handleDragEnd = (e: DragEndEvent) => {
    setEnableDrag(false);
    if (onDragEnd) onDragEnd(e);
  };

  const { dragProps, dragButtonProps, isDragging } = useDrag({
    onDragStart,
    onDragEnd: handleDragEnd,
    onDragMove,
    hasDragButton: true,
    getAllowedDropOperations: () => ["move"],
    getItems,
    preview: previewRef,
  });

  return {
    containerProps: { ...dragProps, draggable: enableDrag },
    childProps: { draggable: false },
    handleCSS: [defaultStyle, isDragging && draggingStyle],
    handleProps: {
      ...dragButtonProps,
      onMouseDown: () => setEnableDrag(true),
      onMouseUp: () => setEnableDrag(false),
    },
    previewRef,
  };
};
